<template>
  <div class="container mx-auto px-4 py-4">
    <div class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <div>
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">Genres</h1>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Total: {{ genres.length }} genres
            </p>
          </div>
          <div class="flex space-x-3">
            <Button label="Add Genre" severity="primary" icon="pi pi-plus" @click="openAddModal" />
          </div>
        </div>
      </div>

      <div class="p-6">
        <!-- Error Message -->
        <div
          v-if="error"
          class="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div class="mt-2 text-sm text-red-700 dark:text-red-300">{{ error }}</div>
            </div>
          </div>
        </div>

        <div v-if="loading" class="text-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>

        <div v-else-if="genres.length === 0" class="text-center py-8">
          <p class="text-gray-500 dark:text-gray-400">
            No genres found. Add your first genre to get started!
          </p>
        </div>

        <div v-else>
          <DataTable
            :value="genres"
            :loading="loading"
            striped-rows
            paginator
            :rows="rows"
            :rows-per-page-options="[10, 25, 50, 100]"
            class="p-datatable-sm"
            :pt="{
              root: { class: 'border border-gray-200 rounded-lg' }
            }"
          >
            <Column field="name" header="Name" sortable>
              <template #body="{ data }">
                <div class="text-sm font-medium text-gray-900 dark:text-white">{{ data.name }}</div>
              </template>
            </Column>

            <Column field="description" header="Description" sortable>
              <template #body="{ data }">
                <div class="text-sm text-gray-900 dark:text-gray-200">
                  {{ data.description || "-" }}
                </div>
              </template>
            </Column>

            <Column field="songCount" header="Song Count" sortable>
              <template #body="{ data }">
                <div class="text-sm text-gray-900 dark:text-gray-200">
                  {{ data.songCount || 0 }}
                </div>
              </template>
            </Column>

            <Column header="Actions" :exportable="false" style="width: 100px">
              <template #body="{ data }">
                <div class="flex items-center gap-2">
                  <button
                    class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    title="Edit genre"
                    @click="openEditModal(data)"
                  >
                    <PencilIcon class="h-5 w-5" />
                  </button>
                  <button
                    :disabled="deletingGenreId === (data.id || data.name)"
                    class="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    :title="
                      deletingGenreId === (data.id || data.name) ? 'Deleting...' : 'Delete genre'
                    "
                    @click="handleDeleteGenre(data)"
                  >
                    <TrashIcon class="h-5 w-5" />
                  </button>
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>

    <!-- Add/Edit Genre Modal -->
    <div
      v-if="showGenreModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="cancelGenreModal"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4"
      >
        <div class="p-6">
          <div class="mb-6">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
              {{ editingGenre ? "Edit Genre" : "Add Genre" }}
            </h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {{ editingGenre ? "Update genre information" : "Create a new song genre" }}
            </p>
          </div>

          <form class="space-y-4" @submit.prevent="handleSaveGenre">
            <div>
              <label
                for="genre-name"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >Name</label
              >
              <input
                id="genre-name"
                v-model="genreForm.name"
                type="text"
                required
                class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter genre name"
              />
            </div>

            <div>
              <label
                for="genre-description"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >Description</label
              >
              <textarea
                id="genre-description"
                v-model="genreForm.description"
                rows="3"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter genre description (optional)"
              ></textarea>
            </div>

            <div
              v-if="genreError"
              class="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3"
            >
              {{ genreError }}
            </div>

            <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button type="button" label="Cancel" severity="secondary" @click="cancelGenreModal" />
              <Button
                type="submit"
                :disabled="genreLoading"
                :label="genreLoading ? 'Saving...' : 'Save'"
                severity="primary"
                :loading="genreLoading"
              />
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="cancelDelete"
    >
      <div class="bg-white rounded-lg border border-gray-200 max-w-md w-full mx-4">
        <div class="p-6">
          <div class="flex items-center mb-4">
            <div
              class="flex-shrink-0 mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100"
            >
              <svg
                class="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>
          <h3 class="text-lg font-medium text-gray-900 text-center mb-2">Delete Genre</h3>
          <p class="text-sm text-gray-500 text-center mb-6">
            Are you sure you want to delete <strong>{{ genreToDelete?.name }}</strong
            >? This action cannot be undone.
          </p>
          <div class="flex space-x-3">
            <Button label="Cancel" severity="secondary" class="flex-1" @click="cancelDelete" />
            <Button
              :disabled="deletingGenreId !== null"
              :label="deletingGenreId !== null ? 'Deleting...' : 'Delete'"
              severity="danger"
              :loading="deletingGenreId !== null"
              class="flex-1"
              @click="confirmDelete"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useToast } from "primevue/usetoast";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import { PencilIcon, TrashIcon } from "@heroicons/vue/24/outline";
import api from "../../services/api";

export default {
  name: "AdminGenres",
  components: {
    DataTable,
    Column,
    Button,
    PencilIcon,
    TrashIcon
  },
  setup() {
    const toast = useToast();
    const genres = ref([]);
    const loading = ref(true);
    const error = ref("");
    const showGenreModal = ref(false);
    const editingGenre = ref(null);
    const genreForm = ref({
      name: "",
      description: ""
    });
    const genreError = ref("");
    const genreLoading = ref(false);
    const showDeleteModal = ref(false);
    const genreToDelete = ref(null);
    const deletingGenreId = ref(null);
    const rows = ref(10);

    const fetchGenres = async () => {
      try {
        loading.value = true;
        error.value = "";
        const response = await api.get("/api/genres");
        if (response.data.success) {
          genres.value = response.data.data.genres || [];
        } else {
          error.value = response.data.message || "Failed to fetch genres";
        }
      } catch (err) {
        console.error("Failed to fetch genres:", err);
        error.value = err.response?.data?.message || "Failed to fetch genres";
      } finally {
        loading.value = false;
      }
    };

    const openAddModal = () => {
      editingGenre.value = null;
      genreForm.value = {
        name: "",
        description: ""
      };
      genreError.value = "";
      showGenreModal.value = true;
    };

    const openEditModal = (genre) => {
      editingGenre.value = genre;
      genreForm.value = {
        name: genre.name,
        description: genre.description || ""
      };
      genreError.value = "";
      showGenreModal.value = true;
    };

    const cancelGenreModal = () => {
      showGenreModal.value = false;
      editingGenre.value = null;
      genreForm.value = {
        name: "",
        description: ""
      };
      genreError.value = "";
    };

    const handleSaveGenre = async () => {
      try {
        genreLoading.value = true;
        genreError.value = "";

        const url = editingGenre.value
          ? `/api/genres/${editingGenre.value.id || editingGenre.value.name}`
          : "/api/genres";

        const method = editingGenre.value ? "put" : "post";

        const response = await api[method](url, {
          name: genreForm.value.name,
          description: genreForm.value.description
        });

        if (response.data.success) {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: editingGenre.value
              ? "Genre updated successfully"
              : "Genre created successfully",
            life: 3000
          });

          await fetchGenres();
          cancelGenreModal();
        } else {
          genreError.value = response.data.message || "Failed to save genre";
        }
      } catch (err) {
        console.error("Failed to save genre:", err);
        genreError.value =
          err.response?.data?.message ||
          err.response?.data?.errors?.join(", ") ||
          "Failed to save genre";
      } finally {
        genreLoading.value = false;
      }
    };

    const handleDeleteGenre = (genre) => {
      genreToDelete.value = genre;
      showDeleteModal.value = true;
    };

    const cancelDelete = () => {
      showDeleteModal.value = false;
      genreToDelete.value = null;
    };

    const confirmDelete = async () => {
      if (!genreToDelete.value) return;

      try {
        deletingGenreId.value = genreToDelete.value.id || genreToDelete.value.name;
        error.value = "";

        const response = await api.delete(`/api/genres/${deletingGenreId.value}`);

        if (response.data.success) {
          genres.value = genres.value.filter((g) => (g.id || g.name) !== deletingGenreId.value);
          showDeleteModal.value = false;
          genreToDelete.value = null;
        } else {
          error.value = response.data.message || "Failed to delete genre";
        }
      } catch (err) {
        console.error("Failed to delete genre:", err);
        error.value = err.response?.data?.message || "Failed to delete genre";
      } finally {
        deletingGenreId.value = null;
      }
    };

    onMounted(() => {
      fetchGenres();
    });

    return {
      genres,
      loading,
      error,
      fetchGenres,
      showGenreModal,
      editingGenre,
      genreForm,
      genreError,
      genreLoading,
      openAddModal,
      openEditModal,
      cancelGenreModal,
      handleSaveGenre,
      handleDeleteGenre,
      showDeleteModal,
      genreToDelete,
      deletingGenreId,
      cancelDelete,
      confirmDelete,
      rows
    };
  }
};
</script>
